<script setup lang="ts">
import { computed, ref, watch } from 'vue';

type LieuOpt = { id: string; nom: string };

const props = defineProps<{
  title: string;
  lieuxOptions: LieuOpt[];
  compact?: boolean; // pour la version du bas (espacement réduit)
  courseTypeFilterEnabled?: boolean;
}>();

// Vue 3.4+: deux v-model contrôlés par le parent
const selectedLieuIds = defineModel<string[]>('selectedLieuIds', { default: [] });
const weekOffset = defineModel<number>('weekOffset', { default: 0 });
const selectedCourseType = defineModel<'all' | 'atelier'>('selectedCourseType', { default: 'all' });

const emit = defineEmits<{ (e: 'prev'): void; (e: 'next'): void; (e: 'reset'): void }>();

const courseTypeFilterEnabled = computed(() => props.courseTypeFilterEnabled !== false);
const isAtelier = computed(() => courseTypeFilterEnabled.value && selectedCourseType.value === 'atelier');
const canPrev = computed(() => !isAtelier.value && weekOffset.value > 0);
const canReset = computed(() => !isAtelier.value && weekOffset.value !== 0);
const filtersOpen = ref(false);
const hasActiveFilters = computed(
  () => selectedLieuIds.value.length > 0 || (courseTypeFilterEnabled.value && selectedCourseType.value !== 'all'),
);

watch(
  () => props.compact,
  (val) => {
    if (val) {
      filtersOpen.value = false;
    }
  },
);

function toggleFilters() {
  filtersOpen.value = !filtersOpen.value;
}

function toggleLieu(id: string) {
  // exclusive toggle
  selectedLieuIds.value = selectedLieuIds.value.includes(id) ? [] : [id];
  // const i = selectedLieuIds.value.indexOf(id);
  // if (i >= 0) selectedLieuIds.value.splice(i, 1);
  // else selectedLieuIds.value.push(id);
}
function clearLieux() {
  selectedLieuIds.value = [];
}
</script>

<template>
  <div :class="['flex flex-col items-center gap-3 w-full', compact ? 'my-2' : 'my-4']">
    <div class="flex items-center gap-4 flex-wrap justify-center">
      <span class="text-sm font-medium opacity-80">{{ title }}</span>
      <div v-if="!isAtelier" class="flex gap-2">
        <button class="btn" @click="emit('prev')" :disabled="!canPrev">
          <font-awesome :icon="['fas', 'chevron-left']" class="mr-1" />
        </button>
        <button class="btn" @click="emit('next')">
          <font-awesome :icon="['fas', 'chevron-right']" class="ml-1" />
        </button>
        <button class="btn" @click="emit('reset')" :disabled="!canReset">
          <font-awesome :icon="['fas', 'calendar-day']" class="mr-1" />
          Aujourd’hui
        </button>
      </div>
    </div>
    <div
      v-if="!compact"
      class="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full max-w-xl px-2 sm:px-6 mx-auto"
    >
      <button
        class="btn border flex items-center gap-2 px-3 py-1 text-xs uppercase tracking-wide hover:bg-white/70 transition self-start relative"
        :class="[{ 'bg-ocreClair/60 border-ocreMedium': hasActiveFilters && !filtersOpen }]"
        :aria-expanded="filtersOpen"
        @click="toggleFilters"
        title="Filtres"
      >
        <font-awesome :icon="['fas', 'sliders']" class="text-sm" />
        <span>Filtres</span>
        <font-awesome
          :icon="['fas', filtersOpen ? 'chevron-left' : 'chevron-right']"
          class="text-[10px] ml-1 opacity-70 transition-transform"
        />
        <span
          v-if="hasActiveFilters && !filtersOpen"
          class="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-emerald-500 shadow ring-2 ring-white"
          aria-hidden="true"
        />
      </button>

      <div class="flex-1 flex justify-center w-full">
        <Transition name="filters-collapse">
          <div v-show="filtersOpen" class="flex flex-col items-center gap-2 w-full sm:w-auto">
            <!-- Filtres type -->
            <div v-if="courseTypeFilterEnabled" class="flex flex-wrap gap-2 items-center justify-center">
              <span class="text-xs uppercase tracking-wide opacity-70">Type</span>
              <button
                class="px-2 py-1 rounded border text-sm"
                :class="[{ 'bg-ocreClair border-ocreMedium': selectedCourseType === 'all' }]"
                @click="selectedCourseType = 'all'"
              >
                Hebdo + ateliers
              </button>
              <button
                class="px-2 py-1 rounded border text-sm"
                :class="[{ 'bg-ocreClair border-ocreMedium': selectedCourseType === 'atelier' }]"
                @click="selectedCourseType = 'atelier'"
              >
                Ateliers
              </button>
            </div>

            <!-- Filtres lieux -->
            <div class="flex flex-wrap gap-2 items-center justify-center">
              <span class="text-xs uppercase tracking-wide opacity-70">Lieu</span>
              <button
                class="px-2 py-1 rounded border text-sm"
                :class="[{ 'bg-ocreClair border-ocreMedium': selectedLieuIds.length === 0 }]"
                @click="clearLieux"
                title="Tous les lieux"
              >
                Tous
              </button>
              <button
                v-for="l in lieuxOptions"
                :key="l.id"
                class="px-2 py-1 rounded border text-sm"
                :class="[{ 'bg-ocreClair border-ocreMedium': selectedLieuIds.includes(l.id) }]"
                @click="toggleLieu(l.id)"
              >
                {{ l.nom }}
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </div>
  </div>
</template>

<style scoped>
.filters-collapse-enter-active,
.filters-collapse-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.filters-collapse-enter-from,
.filters-collapse-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
